import { apiService } from './api';
import { isTWA } from '../utils/platform';
import {
  isCapacitorNative,
  isNativeIOS,
  isNativeAndroid,
  PlayBilling,
  getStoreKitProducts,
  purchaseStoreKitProduct,
  restoreStoreKitPurchases,
} from '../utils/capacitor';

export interface IAPProduct {
  id: string;
  title: string;
  description: string;
  price: string;
  currency: string;
  period?: 'monthly' | 'yearly';
}

export interface IAPPurchaseResult {
  success: boolean;
  message: string;
  receiptData?: string;
  purchaseToken?: string;
  productId?: string;
  packageName?: string;
}

const PLAY_BILLING_METHOD = 'https://play.google.com/billing';
const PACKAGE_NAME = 'com.onrender.fuel_iq.twa';
// Separate product IDs per billing period — required because the TWA Digital Goods API
// can only see one base plan per subscription product (android-browser-helper #449).
const PRODUCT_ID_MONTHLY = 'fueltron_pro_monthly';
const PRODUCT_ID_YEARLY = 'fueltron_pro_yearly';
const PRODUCT_IDS = [PRODUCT_ID_MONTHLY, PRODUCT_ID_YEARLY];

class IAPService {
  private digitalGoodsService: DigitalGoodsService | null = null;

  private isSupported(): boolean {
    return (
      isTWA() ||
      !!(window as any).ReactNativeWebView ||
      !!(window as any).Capacitor ||
      !!(window as any).cordova ||
      'serviceWorker' in navigator
    );
  }

  private detectPlatform(): 'ios' | 'android' | 'web' {
    if (isTWA() || isNativeAndroid()) return 'android';
    const userAgent = navigator.userAgent.toLowerCase();
    if (/iphone|ipad|ipod/.test(userAgent)) return 'ios';
    if (/android/.test(userAgent)) return 'android';
    return 'web';
  }

  async initialize(): Promise<boolean> {
    // Native apps: StoreKit 2 (iPhone) or Play Billing (Android) is always on the device
    if (isCapacitorNative()) {
      return true;
    }

    if (!this.isSupported()) {
      return false;
    }

    // TWA: initialize Digital Goods API
    if (isTWA() && 'getDigitalGoodsService' in window) {
      try {
        this.digitalGoodsService = await window.getDigitalGoodsService(PLAY_BILLING_METHOD);
        return true;
      } catch (err) {
        console.error('Failed to initialize Digital Goods API:', err);
        return false;
      }
    }

    return true;
  }

  async getProducts(): Promise<IAPProduct[]> {
    // Android app: Google Play via PlayBillingPlugin.java
    if (isNativeAndroid()) {
      try {
        const { products: playProducts } = await PlayBilling.getProducts({ productIds: PRODUCT_IDS });
        if (playProducts.length > 0) {
          const products: IAPProduct[] = playProducts.map((p) => ({
            id: p.id,
            title: p.title,
            description: p.description,
            price: String(p.priceValue),
            currency: p.currency,
            period: p.period === 'yearly' ? 'yearly' : 'monthly',
          }));
          products.sort((a) => (a.period === 'monthly' ? -1 : 1));
          return products;
        }
      } catch (err) {
        console.error('Failed to fetch products from Google Play:', err);
      }
    }

    // Capacitor iOS: fetch from App Store via StoreKit 2
    if (isNativeIOS()) {
      try {
        const skProducts = await getStoreKitProducts(PRODUCT_IDS);
        if (skProducts.length > 0) {
          const products: IAPProduct[] = skProducts.map((p) => ({
            id: p.id,
            title: p.title,
            description: p.description,
            price: String(p.priceValue || parseFloat(p.price.replace(/[^0-9.]/g, '')) || 0),
            currency: p.currency,
            period: p.period === 'yearly' ? 'yearly' : 'monthly',
          }));
          products.sort((a, b) => (a.period === 'monthly' ? -1 : 1));
          return products;
        }
      } catch (err) {
        console.error('Failed to fetch products from App Store:', err);
      }
    }

    // TWA: fetch real product details from Google Play.
    if (this.digitalGoodsService) {
      try {
        const details = await this.digitalGoodsService.getDetails(PRODUCT_IDS);
        if (details.length > 0) {
          const products: IAPProduct[] = details.map((item) => ({
            id: item.itemId,
            title: item.title,
            description: item.description,
            price: item.price.value,
            currency: item.price.currency,
            period: item.itemId === PRODUCT_ID_YEARLY ? 'yearly' : 'monthly',
          }));
          products.sort((a, b) => (a.period === 'monthly' ? -1 : 1));
          return products;
        }
      } catch (err) {
        console.error('Failed to fetch product details from Play Store:', err);
      }
    }

    // Fallback: hardcoded product info
    return [
      {
        id: PRODUCT_ID_MONTHLY,
        title: 'FuelTron Pro Monthly',
        description: 'Unlimited AI features, device sync, and advanced analytics',
        price: '7.99',
        currency: 'USD',
        period: 'monthly',
      },
      {
        id: PRODUCT_ID_YEARLY,
        title: 'FuelTron Pro Annual',
        description: 'Unlimited AI features — save 27%',
        price: '69.99',
        currency: 'USD',
        period: 'yearly',
      },
    ];
  }

  async purchaseProduct(productId: string): Promise<IAPPurchaseResult> {
    // Capacitor iOS: purchase via StoreKit 2
    if (isNativeIOS()) {
      return this.purchaseViaStoreKit(productId);
    }

    // Android app: Play Billing Library
    if (isNativeAndroid()) {
      return this.purchaseViaPlayBilling(productId);
    }

    // TWA: use PaymentRequest API with Play Billing
    if (this.digitalGoodsService) {
      return this.purchaseViaTWA(productId);
    }

    const platform = this.detectPlatform();

    // Mobile-web users (Safari on iPhone, Chrome on Android not-in-TWA) end up
    // here because shouldShowIAP() lets them in, but there's no purchase path
    // from a browser — Apple/Google require the native app for IAP. Send them
    // to the right store instead of the old "Native app shell not available"
    // technical error.
    if (platform === 'ios') {
      return {
        success: false,
        message: 'To subscribe, open FuelTron in the iOS app. Install from the App Store: https://apps.apple.com/app/id6762360922',
      };
    }

    if (platform === 'android') {
      return {
        success: false,
        message: 'To subscribe, open FuelTron in the Android app. Install from Google Play: https://play.google.com/store/apps/details?id=com.onrender.fuel_iq.twa',
      };
    }

    if (platform === 'web') {
      return {
        success: false,
        message: 'Subscriptions are only available in the FuelTron mobile app. Open this site on your phone and install the app.',
      };
    }

    if (process.env.NODE_ENV === 'development') {
      return { success: true, message: 'Development mode: Purchase simulation successful' };
    }

    return { success: false, message: 'Subscriptions are only available in the FuelTron mobile app.' };
  }

  private async purchaseViaStoreKit(productId: string): Promise<IAPPurchaseResult> {
    try {
      const result = await purchaseStoreKitProduct(productId);

      if (result.cancelled) {
        return { success: false, message: 'Purchase was cancelled' };
      }

      if (result.pending) {
        return { success: false, message: 'Purchase is pending approval (e.g. Ask to Buy). You\'ll be upgraded once approved.' };
      }

      if (result.success && result.signedTransactionInfo) {
        const confirmResponse = await apiService.post('/iap/confirm', {
          platform: 'ios',
          receiptData: result.signedTransactionInfo,
          productId: result.productId || productId,
        });

        if (confirmResponse.data.success) {
          return {
            success: true,
            message: 'Purchase successful! Pro features are now active.',
            receiptData: result.signedTransactionInfo,
            productId: result.productId || productId,
          };
        }

        return { success: false, message: 'Purchase verification failed on server. Please try restoring purchases.' };
      }

      return { success: false, message: result.message || 'Purchase failed' };
    } catch (error: any) {
      return { success: false, message: error.message || 'Purchase failed. Please try again.' };
    }
  }

  private async purchaseViaPlayBilling(productId: string): Promise<IAPPurchaseResult> {
    try {
      // Play needs the product details loaded in this session before it can sell one.
      await PlayBilling.getProducts({ productIds: PRODUCT_IDS });
      const result = await PlayBilling.purchase({ productId });
      if (result.cancelled) return { success: false, message: 'Purchase was cancelled' };
      if (result.pending) {
        return { success: false, message: 'Your payment is pending. You will be upgraded once Google Play confirms it.' };
      }
      if (!result.success || !result.purchaseToken) return { success: false, message: 'Purchase failed' };

      const confirmResponse = await apiService.post('/iap/confirm', {
        platform: 'android',
        packageName: PACKAGE_NAME,
        productId: result.productId || productId,
        purchaseToken: result.purchaseToken,
        receiptData: result.purchaseToken,
      });
      if (confirmResponse.data.success) {
        return {
          success: true,
          message: 'Purchase successful! Pro features are now active.',
          purchaseToken: result.purchaseToken,
          productId: result.productId || productId,
          packageName: PACKAGE_NAME,
        };
      }
      return { success: false, message: 'Purchase verification failed on server. Please try restoring purchases.' };
    } catch (error: any) {
      return { success: false, message: error?.message || 'Purchase failed. Please try again.' };
    }
  }

  private async purchaseViaTWA(productId: string): Promise<IAPPurchaseResult> {
    try {
      const paymentMethods = [
        {
          supportedMethods: PLAY_BILLING_METHOD,
          data: { sku: productId },
        },
      ];

      const paymentDetails = {
        total: {
          label: 'FuelTron Pro',
          amount: { currency: 'USD', value: '7.99' },
        },
      };

      const request = new PaymentRequest(paymentMethods, paymentDetails);

      // Check if the billing method is actually supported before showing
      const canMakePayment = await request.canMakePayment();
      if (!canMakePayment) {
        return {
          success: false,
          message: 'Google Play billing is not available. Please try again from the Google Play Store, or contact support at contact@decisivelabsllc.com.',
        };
      }

      const response = await request.show();

      // Extract purchase token from the response
      const { purchaseToken } = response.details as any;

      if (!purchaseToken) {
        await response.complete('fail');
        return { success: false, message: 'No purchase token received from Google Play' };
      }

      // Verify with backend
      const confirmResponse = await apiService.post('/iap/confirm', {
        platform: 'android',
        packageName: PACKAGE_NAME,
        productId,
        purchaseToken,
        receiptData: purchaseToken,
      });

      if (confirmResponse.data.success) {
        // Acknowledgement is handled server-side via androidpublisher API.
        // Do NOT call consume() here — that's for one-time purchases, not subscriptions.
        await response.complete('success');
        return {
          success: true,
          message: 'Purchase successful! Pro features are now active.',
          purchaseToken,
          productId,
          packageName: PACKAGE_NAME,
        };
      }

      await response.complete('fail');
      return { success: false, message: 'Purchase verification failed on server' };
    } catch (error: any) {
      if (error.name === 'AbortError') {
        return { success: false, message: 'Purchase was cancelled' };
      }
      // Catch "payment method not supported" and similar PaymentRequest errors
      if (error.message?.includes('not supported') || error.name === 'NotSupportedError') {
        return {
          success: false,
          message: 'Google Play billing is temporarily unavailable. Please try updating the app from the Play Store, or contact support at contact@decisivelabsllc.com.',
        };
      }
      return { success: false, message: error.message || 'Purchase failed. Please try again.' };
    }
  }

  async confirmPurchase(
    platform: 'ios' | 'android',
    receiptData: string,
    productId: string,
    purchaseToken?: string,
    packageName?: string
  ): Promise<boolean> {
    try {
      const response = await apiService.post('/iap/confirm', {
        platform,
        receiptData,
        productId,
        purchaseToken,
        packageName,
      });
      return response.data.success;
    } catch (error: any) {
      console.error('Failed to confirm purchase:', error);
      return false;
    }
  }

  async restorePurchases(): Promise<IAPPurchaseResult> {
    // Android app: re-send every active Play subscription to the server
    if (isNativeAndroid()) {
      try {
        const { purchases } = await PlayBilling.restorePurchases();
        if (purchases.length === 0) return { success: false, message: 'No active subscriptions found' };
        for (const purchase of purchases) {
          await apiService.post('/iap/confirm', {
            platform: 'android',
            packageName: PACKAGE_NAME,
            productId: purchase.productId,
            purchaseToken: purchase.purchaseToken,
            receiptData: purchase.purchaseToken,
          });
        }
        return { success: true, message: 'Purchases restored successfully' };
      } catch (error: any) {
        return { success: false, message: error?.message || 'Restore failed' };
      }
    }

    // Capacitor iOS: restore via StoreKit 2
    if (isNativeIOS()) {
      try {
        const result = await restoreStoreKitPurchases();
        if (!result.success || result.transactions.length === 0) {
          return { success: false, message: result.message || 'No active subscriptions found' };
        }

        for (const tx of result.transactions) {
          await apiService.post('/iap/confirm', {
            platform: 'ios',
            receiptData: tx.signedTransactionInfo,
            productId: tx.productId,
          });
        }

        return { success: true, message: 'Purchases restored successfully!' };
      } catch (error: any) {
        return { success: false, message: error.message || 'Restore failed' };
      }
    }

    // TWA: use Digital Goods API to list existing purchases
    if (this.digitalGoodsService) {
      try {
        const purchases = await this.digitalGoodsService.listPurchases();
        if (purchases.length === 0) {
          return { success: false, message: 'No active purchases found' };
        }

        for (const purchase of purchases) {
          await apiService.post('/iap/confirm', {
            platform: 'android',
            packageName: PACKAGE_NAME,
            productId: purchase.itemId,
            purchaseToken: purchase.purchaseToken,
            receiptData: purchase.purchaseToken,
          });
        }

        return { success: true, message: 'Purchases restored successfully' };
      } catch (error: any) {
        return { success: false, message: error.message || 'Restore failed' };
      }
    }

    return { success: false, message: 'Purchase restoration is not available in this environment' };
  }

  async checkSubscriptionStatus(): Promise<boolean> {
    try {
      const response = await apiService.getMe();
      return response.user?.tier === 'pro';
    } catch (error) {
      console.error('Failed to check subscription status:', error);
      return false;
    }
  }
}

export const iapService = new IAPService();
export default iapService;
